import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';

import { remove as removeLocalStorageItem } from '~libs/localstorage';
import { clearKeys } from '~libs/sessionKey';
import { rejectReauthWaiters } from '~libs/sessionReauth';
import { publishSessionMessage } from '~libs/sessionSync';

import { Spinner } from '~components';

import { logout } from '~main/actions/session';

import { removeAuthenticated } from '~main/reducers/login';

const Logout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [signedOut, setSignedOut] = useState(false);

  // Signing out revokes the session on the API, so it must run exactly once even when StrictMode replays
  // the effect.
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const signOut = async () => {
      // Never rejects. Client state is cleared whether or not the API call worked.
      await dispatch(logout());
      // The key is deleted on an explicit sign-out only, so an expiring stale tab can never delete a key
      // another tab has just created.
      await clearKeys();
      rejectReauthWaiters('signed-out');
      dispatch(removeAuthenticated('signed-out'));
      publishSessionMessage({ type: 'signed-out', reason: 'signed-out' });
      removeLocalStorageItem('dashboard-visited');
      setSignedOut(true);
    };

    signOut();
  }, []);

  return (
    <div className="d-flex justify-content-center mt-5 login">
      <Card className="rounded">
        <CardHeader className="d-flex justify-content-center bg-menu-background">
          <img src="/assets/primary_logo.png" alt="Logo" />
        </CardHeader>
        <CardBody className="d-flex flex-column text-center justify-content-center">
          {signedOut ? (
            <>
              <h3 data-cy={'logout-header'}>You are now logged out</h3>
              <Button onClick={() => navigate('/login')} className="w-25 mx-auto mt-3" color="success">
                Log back in
              </Button>
            </>
          ) : (
            <>
              <Spinner loading={true} type="beat" className="mt-4" />
              <h3 className="mt-4" data-cy="logout-progress">
                Signing you out
              </h3>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default Logout;
